import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLeaderboard, type LeaderboardHistory } from '../api';

const COLORS: Record<string, string> = {
  Armands: '#3fd66a',
  Edgars: '#f87171',
  Valters: '#60a5fa',
  'Emīls': '#fbbf24',
  'Einārs': '#a78bfa',
  Raivis: '#22d3ee',
  Edijs: '#f472b6',
  'Jānis': '#fb923c',
};

const PADL = 36;
const PADR = 16;
const PADT = 12;
const PADB = 26;

type Mode = 'actual' | 'predicted';

export default function StandingsHistoryChart({ data }: { data: LeaderboardHistory | undefined }) {
  const dates = data?.dates ?? [];
  const series = data?.series ?? [];

  const tournamentStarted = useMemo(
    () => series.some((s) => s.actual.some((v) => v > 0)),
    [series],
  );

  if (dates.length === 0) {
    return (
      <section className="card p-5 sm:p-6 text-center">
        <div className="font-display text-lg font-semibold mb-1">Standings trend</div>
        <div className="text-xs text-dim max-w-md mx-auto">
          A prediction snapshot will appear once daily snapshots populate.
        </div>
      </section>
    );
  }

  if (!tournamentStarted) {
    return <PreTournament />;
  }

  return <Progression dates={dates} series={series} />;
}

type PreSource = 'bookmaker' | 'ai';

function PreTournament() {
  const { data } = useQuery({ queryKey: ['leaderboard'], queryFn: getLeaderboard });
  const [source, setSource] = useState<PreSource>('bookmaker');

  const standings = data?.standings ?? [];
  const ranked = useMemo(() => (
    [...standings]
      .map((s) => ({
        player: s.name,
        value: source === 'bookmaker' ? s.projectedPoints : s.aiProjectedPoints,
      }))
      .sort((a, b) => b.value - a.value)
  ), [standings, source]);
  const maxV = Math.max(1, ...ranked.map((r) => r.value));

  return (
    <section className="card p-3 sm:p-4">
      <div className="flex items-baseline justify-between mb-1.5 gap-2">
        <div>
          <div className="font-display text-lg font-semibold leading-none">Pre-tournament prediction</div>
          <div className="text-[10px] uppercase tracking-wider text-dim mt-1">
            {source === 'bookmaker' ? 'Bookmaker odds' : 'AI (Llama 3.3 70B)'}
          </div>
        </div>
        <div className="flex rounded-md bg-ink-800 p-0.5 text-[10px]">
          <button
            type="button"
            onClick={() => setSource('bookmaker')}
            className={`px-2 py-1 rounded transition-colors ${source === 'bookmaker' ? 'bg-ink-700 text-chalk' : 'text-dim'}`}
          >Bookmaker</button>
          <button
            type="button"
            onClick={() => setSource('ai')}
            className={`px-2 py-1 rounded transition-colors ${source === 'ai' ? 'bg-ink-700 text-cyan-400' : 'text-dim'}`}
          >AI</button>
        </div>
      </div>
      <p className="text-[11px] text-dim mb-3">
        {source === 'bookmaker'
          ? 'Projected total if the 1 X 2 market is right. Probability-weighted expected value per match.'
          : 'AI picks an outright result per match (no probabilistic hedging), so totals run a bit higher.'}
      </p>
      <div className="space-y-2">
        {ranked.map((r, i) => {
          const color = COLORS[r.player] ?? '#9aa3b2';
          const width = (r.value / maxV) * 100;
          return (
            <div key={r.player} className="flex items-center gap-2.5 text-sm">
              <span className="w-5 text-right stat text-dim">{i + 1}.</span>
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} />
              <span className="w-16 sm:w-20 truncate">{r.player}</span>
              <div className="flex-1 h-2 rounded-full bg-ink-800 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${width}%`, backgroundColor: color }} />
              </div>
              <span className={`stat w-10 text-right ${source === 'ai' ? 'text-cyan-400' : 'text-pitch-400'}`}>{r.value.toFixed(1)}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function niceTicks(lo: number, hi: number, count = 5): number[] {
  if (hi <= lo) return [lo];
  const range = hi - lo;
  const rough = range / (count - 1);
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const candidates = [1, 2, 2.5, 5, 10].map((c) => c * pow);
  const step = candidates.find((c) => c >= rough) ?? candidates[candidates.length - 1];
  const start = Math.floor(lo / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= hi + step / 2; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
}

function Progression({ dates: rawDates, series: rawSeries }: { dates: string[]; series: LeaderboardHistory['series'] }) {
  const [mode, setMode] = useState<Mode>('actual');
  const [hovered, setHovered] = useState<string | null>(null);     // legend hover (preview)
  const [isolated, setIsolated] = useState<string | null>(null);   // legend click (sticky)
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);   // date column hovered for tooltip
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(800);

  // Trim leading pre-tournament days. Keep one immediate-pre-start baseline so the
  // first sloped segment shows the jump from "nothing happened yet" to first results.
  const { dates, series } = useMemo(() => {
    if (rawDates.length === 0) return { dates: rawDates, series: rawSeries };
    let firstNonZero = rawDates.length;
    for (let i = 0; i < rawDates.length; i++) {
      if (rawSeries.some((s) => (s.actual[i] ?? 0) > 0)) { firstNonZero = i; break; }
    }
    if (firstNonZero === rawDates.length) return { dates: rawDates, series: rawSeries };
    const start = Math.max(0, firstNonZero - 1);
    if (start === 0) return { dates: rawDates, series: rawSeries };
    return {
      dates: rawDates.slice(start),
      series: rawSeries.map((s) => ({ ...s, actual: s.actual.slice(start), projected: s.projected.slice(start) })),
    };
  }, [rawDates, rawSeries]);

  // Match SVG width to container so text stays at native size and lines aren't horizontally stretched.
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(320, e.contentRect.width)));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const W = width;
  const H = Math.max(220, Math.min(360, Math.round(width * 0.32)));

  // Tighter Y range — clip to actual data ±10% padding, not 0-to-max. Keeps trend lines readable.
  const yRange = useMemo(() => {
    let lo = Infinity, hi = -Infinity;
    for (const s of series) {
      const arr = mode === 'actual' ? s.actual : s.projected;
      for (const v of arr) {
        if (v < lo) lo = v;
        if (v > hi) hi = v;
      }
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { lo: 0, hi: 10 };
    if (hi - lo < 2) hi = lo + 2;
    const pad = Math.max((hi - lo) * 0.15, 1);
    return { lo: Math.max(0, Math.floor(lo - pad)), hi: Math.ceil(hi + pad) };
  }, [series, mode]);

  const xFor = (i: number) =>
    PADL + (W - PADL - PADR) * (dates.length === 1 ? 0.5 : i / (dates.length - 1));
  const yFor = (v: number) =>
    PADT + (H - PADT - PADB) * (1 - (v - yRange.lo) / Math.max(0.0001, yRange.hi - yRange.lo));

  const yTicks = useMemo(() => niceTicks(yRange.lo, yRange.hi, 5), [yRange]);

  const sortedSeries = useMemo(() => {
    return [...series].sort((a, b) => {
      const arrA = mode === 'actual' ? a.actual : a.projected;
      const arrB = mode === 'actual' ? b.actual : b.projected;
      return (arrB[arrB.length - 1] ?? 0) - (arrA[arrA.length - 1] ?? 0);
    });
  }, [series, mode]);

  // Snap hover X to nearest date index
  function onMouseMove(e: React.MouseEvent<SVGSVGElement>) {
    const svg = e.currentTarget;
    const r = svg.getBoundingClientRect();
    const localX = e.clientX - r.left;
    const inner = W - PADL - PADR;
    const t = (localX - PADL) / inner;
    const i = Math.round(t * (dates.length - 1));
    setHoverIdx(Math.max(0, Math.min(dates.length - 1, i)));
  }

  // Build tooltip rows for hovered date, ranked by points
  const tooltipRows = useMemo(() => {
    if (hoverIdx === null) return null;
    const rows = sortedSeries.map((s) => ({
      player: s.player,
      actual: s.actual[hoverIdx] ?? 0,
      projected: s.projected[hoverIdx] ?? 0,
    }));
    rows.sort((a, b) => (mode === 'actual' ? b.actual - a.actual : b.projected - a.projected));
    return rows.map((r, rank) => ({ ...r, rank: rank + 1 }));
  }, [hoverIdx, sortedSeries, mode]);

  // Tooltip placement (right-side if hover is left half, else left-side)
  const tooltipX = hoverIdx === null ? 0 : xFor(hoverIdx);
  const tooltipOnLeft = tooltipX > W / 2;

  return (
    <section className="card p-3 sm:p-4">
      <div className="flex items-baseline justify-between mb-2 gap-2">
        <div>
          <div className="font-display text-lg font-semibold leading-none">Standings trend</div>
          <div className="text-[10px] uppercase tracking-wider text-dim mt-1">{dates[0]} → {dates[dates.length - 1]}</div>
        </div>
        <div className="flex rounded-md bg-ink-800 p-0.5 text-[10px]">
          <ModeBtn current={mode} value="actual" onClick={setMode}>Actual</ModeBtn>
          <ModeBtn current={mode} value="predicted" onClick={setMode}>Predicted</ModeBtn>
        </div>
      </div>
      <div ref={wrapRef} className="relative w-full">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width={W} height={H}
          className="w-full h-auto block"
          onMouseMove={onMouseMove}
          onMouseLeave={() => setHoverIdx(null)}
        >
          {/* Y gridlines + labels */}
          {yTicks.map((tick) => {
            const y = yFor(tick);
            return (
              <g key={tick}>
                <line x1={PADL} x2={W - PADR} y1={y} y2={y} stroke="#1c2230" strokeWidth={1} strokeDasharray={tick === yRange.lo ? '' : '3 5'} />
                <text x={PADL - 6} y={y + 3.5} textAnchor="end" fontSize="10" fill="#9aa3b2" fontFamily="ui-monospace, monospace">
                  {Number.isInteger(tick) ? tick : tick.toFixed(1)}
                </text>
              </g>
            );
          })}
          {/* X tick labels (3 evenly distributed) */}
          {dates.length > 0 && [0, Math.floor(dates.length / 2), dates.length - 1]
            .filter((v, idx, arr) => arr.indexOf(v) === idx)
            .map((i) => {
              const d = dates[i];
              const short = d.slice(5).replace('-', '/');
              return (
                <text key={i} x={xFor(i)} y={H - 8}
                  textAnchor={i === 0 ? 'start' : i === dates.length - 1 ? 'end' : 'middle'}
                  fontSize="10" fill="#9aa3b2" fontFamily="ui-monospace, monospace">
                  {short}
                </text>
              );
            })}
          {/* Lines */}
          {sortedSeries.map((s) => {
            const color = COLORS[s.player] ?? '#9aa3b2';
            const dimmed = (isolated && isolated !== s.player) || (!isolated && hovered && hovered !== s.player);
            const op = dimmed ? 0.12 : 1;
            const values = mode === 'actual' ? s.actual : s.projected;
            const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)}`).join(' ');
            return (
              <g key={s.player}>
                <path
                  d={path}
                  stroke={color}
                  strokeWidth={isolated === s.player ? 2.5 : 2}
                  strokeDasharray={mode === 'predicted' ? '4 4' : ''}
                  fill="none"
                  opacity={op}
                />
                {dates.length === 1 && (
                  <circle cx={xFor(0)} cy={yFor(values[0] ?? 0)} r={3} fill={color} opacity={op} />
                )}
              </g>
            );
          })}
          {/* Hover guide + per-player dots */}
          {hoverIdx !== null && (
            <>
              <line x1={tooltipX} x2={tooltipX} y1={PADT} y2={H - PADB} stroke="#3a4358" strokeWidth={1} strokeDasharray="2 3" />
              {sortedSeries.map((s) => {
                const color = COLORS[s.player] ?? '#9aa3b2';
                const dimmed = (isolated && isolated !== s.player) || (!isolated && hovered && hovered !== s.player);
                const op = dimmed ? 0.12 : 1;
                const v = (mode === 'actual' ? s.actual[hoverIdx] : s.projected[hoverIdx]) ?? 0;
                return <circle key={s.player} cx={tooltipX} cy={yFor(v)} r={3.5} fill={color} stroke="#0b0e16" strokeWidth={1.2} opacity={op} />;
              })}
            </>
          )}
        </svg>

        {/* Floating tooltip */}
        {hoverIdx !== null && tooltipRows && (
          <div
            className="pointer-events-none absolute top-0 z-10 rounded-md border border-ink-700 bg-ink-900/95 backdrop-blur-sm p-2.5 shadow-lg text-[11px]"
            style={{
              left: tooltipOnLeft ? undefined : `calc(${(tooltipX / W) * 100}% + 10px)`,
              right: tooltipOnLeft ? `calc(${(1 - tooltipX / W) * 100}% + 10px)` : undefined,
              minWidth: 180,
            }}
          >
            <div className="text-[10px] uppercase tracking-wider text-dim mb-1">{dates[hoverIdx]}</div>
            <div className="grid grid-cols-[auto_1fr_auto_auto] gap-x-2 gap-y-0.5 items-baseline">
              <div className="text-[9px] text-dim">#</div>
              <div className="text-[9px] text-dim">Player</div>
              <div className="text-[9px] text-dim text-right">Act</div>
              <div className="text-[9px] text-dim text-right">Proj</div>
              {tooltipRows.map((r) => {
                const color = COLORS[r.player] ?? '#9aa3b2';
                const muted = (isolated && isolated !== r.player) || (!isolated && hovered && hovered !== r.player);
                return (
                  <div key={r.player} className={`contents ${muted ? 'opacity-30' : ''}`}>
                    <span className="text-dim font-mono text-[10px]">{r.rank}</span>
                    <span className="flex items-center gap-1.5 truncate">
                      <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: color }} />
                      {r.player}
                    </span>
                    <span className="stat text-right">{Number(r.actual).toFixed(0)}</span>
                    <span className="stat text-right text-pitch-400">{Number(r.projected).toFixed(1)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5 text-xs">
        {sortedSeries.map((s, i) => {
          const color = COLORS[s.player] ?? '#9aa3b2';
          const arr = mode === 'actual' ? s.actual : s.projected;
          const last = arr[arr.length - 1] ?? 0;
          const isHover = hovered === s.player;
          const isIsolated = isolated === s.player;
          return (
            <div
              key={s.player}
              onMouseEnter={() => setHovered(s.player)}
              onMouseLeave={() => setHovered(null)}
              className={`inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 ${isHover ? 'bg-ink-800' : ''} ${isIsolated ? 'ring-1 ring-pitch-500/40' : ''}`}
            >
              <span className="text-[10px] text-dim w-3 text-right">{i + 1}.</span>
              <button
                type="button"
                aria-label={`Isolate ${s.player}`}
                onClick={() => setIsolated(isIsolated ? null : s.player)}
                className="w-3 h-3 rounded-sm shrink-0"
                style={{ backgroundColor: color, opacity: !isolated || isIsolated ? 1 : 0.4 }}
              />
              <button
                type="button"
                onClick={() => setIsolated(isIsolated ? null : s.player)}
                className={isIsolated ? 'text-chalk' : ''}
              >{s.player}</button>
              <span className="stat text-dim">{Number(last).toFixed(mode === 'actual' ? 0 : 1)}</span>
            </div>
          );
        })}
        {isolated && (
          <button
            type="button"
            onClick={() => setIsolated(null)}
            className="text-[10px] text-pitch-400 hover:underline self-center"
          >Clear isolation</button>
        )}
      </div>
    </section>
  );
}

function ModeBtn({ current, value, onClick, children }: { current: Mode; value: Mode; onClick: (m: Mode) => void; children: React.ReactNode }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`px-2 py-1 rounded transition-colors ${active ? 'bg-ink-700 text-chalk' : 'text-dim'}`}
    >{children}</button>
  );
}
